import React from 'react';
import { Link } from 'react-router-dom';
import DNAParticles from '../components/DNAParticles';

export default function Home() {
  return (
    <main>
      {/* ── Hero ── */}
      <section style={{
        position: 'relative', minHeight: '100vh', display: 'flex',
        alignItems: 'center', background: 'var(--bg-primary)', overflow: 'hidden',
        paddingTop: 72,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 60% at 30% 50%, rgba(0,174,239,0.07) 0%, transparent 70%)' }} />
        <DNAParticles />

        <div className="container" style={{ position: 'relative', zIndex: 1, paddingTop: 60, paddingBottom: 60 }}>
          <div style={{ maxWidth: 720 }}>
            <span className="eyebrow fade-in">DNA Methylation Diagnostics · Built on 199,000+ Patient Records</span>
            <h1 className="hero-headline fade-in fade-in-delay-1" style={{ marginBottom: 28, maxWidth: 680 }}>
              Designed to detect leukemia{' '}
              <span style={{ color: 'var(--accent-cyan)' }}>before</span>{' '}
              clinical symptoms appear.
            </h1>
            <p className="body-lg fade-in fade-in-delay-2" style={{ maxWidth: 620, marginBottom: 48 }}>
              Alercell's AI-powered epigenetic diagnostic platform identifies pre-leukemic DNA methylation signatures in peripheral blood — enabling intervention before disease progression, not after.
            </p>
            <div className="fade-in fade-in-delay-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Link to="/platform" className="btn btn-primary" style={{ fontSize: 15, padding: '14px 32px' }}>
                Explore the Platform
              </Link>
              <Link to="/science" className="btn btn-outline" style={{ fontSize: 15, padding: '14px 32px' }}>
                View the Science
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ width: 1, height: 48, background: 'linear-gradient(to bottom, var(--accent-cyan), transparent)', margin: '0 auto' }} />
        </div>
      </section>

      {/* ── Metrics Strip ── */}
      <div className="metric-strip">
        <div className="container">
          <div className="metric-grid">
            {[
              { value: '41', suffix: ' mo', label: 'Pre-symptomatic detection lead time vs. standard of care' },
              { value: '200K+', suffix: '', label: 'De-identified patient EMRs validated (ALER-5 cohort)' },
              { value: 'AUC 0.967', suffix: '', label: 'LENA platform diagnostic accuracy' },
              { value: '10 yr', suffix: '', label: 'Maximum observed pre-symptomatic detection window' },
            ].map((m, i) => (
              <div key={i} className="metric-item">
                <span className="metric-value">{m.value}<span style={{ fontSize: '0.55em', opacity: 0.7 }}>{m.suffix}</span></span>
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Platform Overview ── */}
      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="section-intro" style={{ marginBottom: 8 }}>
            <span className="eyebrow">An Integrated Diagnostic Ecosystem</span>
            <h2 className="section-headline" style={{ marginBottom: 20 }}>
              Seven platforms. One mission: detect cancer before it becomes crisis.
            </h2>
            <p className="body-md" style={{ maxWidth: 720 }}>
              The LENA platform suite combines AI-driven methylation analysis, precision diagnostics, and theranostic guidance into a unified clinical and research ecosystem — from raw epigenetic data to actionable patient risk scores.
            </p>
          </div>

          <div className="platform-grid">
            {[
              { label: 'Diagnostics', title: 'LENA Q51® & LENA S1®', body: 'Commercially available peripheral blood assays for early leukemia and lung cancer detection via DNA methylation analysis. Revenue-generating today.' },
              { label: 'AI Engine', title: 'LENA Code™ & LENA Score™', body: 'Normalizes raw methylation data across platforms and converts epigenetic signals into a quantitative patient risk score developed against 538,090 clinical observations.' },
              { label: 'Platform', title: 'Deep Leukemic Cloud®', body: 'Cloud-based infrastructure housing the ALER-5 dataset — 538,090 clinical observations from 199,800 patient EMRs across 5 cohorts, with methylation reference data across 3 Illumina platforms.' },
              { label: 'Theranostics', title: 'LENA-Rx™ & Epigenetic Clock™', body: 'Two-draw trajectory (KIT) modeling and theranostic guidance — linking risk scores to treatment pathway selection with real-time PubMed and ClinicalTrials.gov integration.' },
            ].map((card, i) => (
              <div key={i} className="card">
                <span className="card-label">{card.label}</span>
                <h3 className="card-title" style={{ marginBottom: 12 }}>{card.title}</h3>
                <p className="body-md">{card.body}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40 }}>
            <Link to="/platform" className="btn btn-text">View All 10 Platforms</Link>
          </div>
        </div>
      </section>

      {/* ── Validation & Evidence ── */}
      <section className="section section-surface">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }} className="validation-grid">
            <div>
              <span className="eyebrow">The Science Behind the Platform</span>
              <h2 className="section-headline" style={{ marginBottom: 20 }}>
                DNA methylation changes precede leukemia by years — not months.
              </h2>
              <div className="cyan-line" />
              <p className="body-md" style={{ marginBottom: 20 }}>
                Every cell in the body carries a DNA methylation pattern — a layer of chemical marks that regulate which genes are active. In pre-leukemic cells, these patterns drift in characteristic ways years before any genetic mutation becomes detectable or any symptom appears.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Alercell's LENA platform reads these methylation signatures from a standard blood draw, compares them against reference patterns developed on more than 199,000 patient records, and generates a quantitative risk score.
              </p>
              <p className="body-md" style={{ marginBottom: 32 }}>
                This makes it possible to identify patients at risk early enough for preventive intervention — not after the disease has advanced to a stage requiring aggressive treatment.
              </p>
              <Link to="/science#validation" className="btn btn-text">Explore the Full Validation Data</Link>
            </div>

            {/* Detection Timeline Visual */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 40 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 32 }}>Disease Progression Timeline</p>
              <div style={{ position: 'relative', paddingBottom: 60 }}>
                {[
                  { label: 'Normal Hematopoiesis', active: false, pos: '0%' },
                  { label: 'CHIP / Methylation Drift', active: true, pos: '25%', alercell: true },
                  { label: 'Driver Mutation', active: false, pos: '55%' },
                  { label: 'AML Diagnosis', active: false, pos: '100%' },
                ].map((stage, i) => (
                  <div key={i} style={{ position: 'absolute', left: stage.pos, transform: 'translateX(-50%)', textAlign: 'center', top: 0 }}>
                    <div style={{
                      width: stage.alercell ? 14 : 10, height: stage.alercell ? 14 : 10,
                      borderRadius: '50%', background: stage.alercell ? 'var(--accent-cyan)' : 'var(--text-muted)',
                      margin: '0 auto 8px',
                      boxShadow: stage.alercell ? '0 0 16px rgba(0,174,239,0.6)' : 'none',
                    }} />
                    {stage.alercell && (
                      <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, whiteSpace: 'nowrap', background: 'rgba(0,174,239,0.15)', border: '1px solid var(--accent-cyan-border)', borderRadius: 6, padding: '4px 10px' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-cyan)' }}>LENA detects here</span>
                      </div>
                    )}
                    <span style={{ fontSize: 10, color: stage.alercell ? 'var(--text-primary)' : 'var(--text-muted)', lineHeight: 1.3, display: 'block', maxWidth: 70 }}>{stage.label}</span>
                  </div>
                ))}
                <div style={{ position: 'absolute', left: 0, right: 0, top: 6, height: 2, background: 'var(--border)', borderRadius: 1 }} />
                <div style={{ position: 'absolute', left: 0, width: '25%', top: 6, height: 2, background: 'var(--accent-cyan)', borderRadius: 1 }} />
              </div>
              <div style={{ marginTop: 60, padding: '16px 20px', background: 'var(--accent-cyan-dim)', border: '1px solid var(--accent-cyan-border)', borderRadius: 8 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)' }}>
                  ← designed to detect before conventional diagnosis
                </span>
              </div>
            </div>
          </div>
        </div>
        <style>{`@media(max-width:768px){.validation-grid{grid-template-columns:1fr!important;}}`}</style>
      </section>



      {/* ── Audience CTAs ── */}
      <section className="section section-surface">
        <div className="container">
          <div className="section-intro-center">
            <h2 className="section-headline" style={{ marginBottom: 12 }}>Where would you like to go?</h2>
            <p className="body-md">The LENA platform serves clinicians, investors, and researchers differently. Find your path.</p>
          </div>
          <div className="audience-grid">
            {[
              {
                icon: '🔬', title: 'Clinicians & Lab Directors',
                desc: 'Evaluate the LENA platform for your practice or laboratory. Learn how peripheral blood methylation analysis can integrate into your diagnostic workflow.',
                cta: 'Evaluate the Platform', path: '/contact#clinical',
              },
              {
                icon: '📈', title: 'Investors',
                desc: 'A validated diagnostic category with no commercial incumbent. Series A financing currently open to qualified investors.',
                cta: 'Investor Relations', path: '/investors',
              },
              {
                icon: '🧬', title: 'Research Partners',
                desc: 'Explore collaboration opportunities in clinical validation, technology licensing, or co-development. The ALER-5 cohort is the largest methylation-survival dataset in hematologic oncology.',
                cta: 'Explore Collaboration', path: '/contact#partner',
              },
            ].map((a, i) => (
              <div key={i} className="audience-card">
                <span className="audience-icon">{a.icon}</span>
                <div className="audience-title">{a.title}</div>
                <p className="audience-desc">{a.desc}</p>
                <Link to={a.path} className="btn btn-outline" style={{ fontSize: 13 }}>{a.cta}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
